import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '@/utils/cn';

type RadioProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: ReactNode;
  hasError?: boolean;
};

/**
 * Sizilianer-Radio mit Custom-Look.
 * Kreis mit gold-Punkt im Checked-State.
 *
 * @example
 *   <Radio name="size" value="small" label="Klein" />
 *   <Radio name="size" value="large" label="Groß" />
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, hasError, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'inline-flex items-center gap-2.5 cursor-pointer group',
        props.disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      <span className="relative shrink-0">
        <input
          ref={ref}
          id={inputId}
          type="radio"
          className="peer sr-only"
          {...props}
        />
        <span
          className={cn(
            'block size-5 rounded-full bg-paper-2',
            'border transition-[color,background-color,border-color,transform,box-shadow]',
            hasError ? 'border-it-red' : 'border-rule',
            'peer-checked:border-gold',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-gold peer-focus-visible:outline-offset-2',
            'group-hover:border-gold',
          )}
        >
          <span
            className={cn(
              'block size-2.5 rounded-full bg-gold',
              'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
              'opacity-0 peer-checked:opacity-100 transition-opacity',
              'pointer-events-none',
            )}
            aria-hidden="true"
          />
        </span>
      </span>
      <span className="font-sans text-sm text-ink-2 select-none">{label}</span>
    </label>
  );
});
